import { forwardRef, type ReactElement } from 'react'
import { Drawer as BaseDrawer } from '@base-ui/react/drawer'
import { cn } from '@/lib/utils'
import { DialogClose, DialogLayout, type DialogContentProps } from './Dialog'
import './drawer.css'

export type DrawerSide = 'right' | 'left' | 'bottom'
export type DrawerSize = 'default' | 'narrow'

const swipeDirection = { right: 'right', left: 'left', bottom: 'down' } as const

type DrawerRootProps = Pick<BaseDrawer.Root.Props, 'open' | 'defaultOpen' | 'onOpenChange' | 'onOpenChangeComplete' | 'disablePointerDismissal'>

export type DrawerProps = DrawerRootProps & DialogContentProps & Omit<BaseDrawer.Popup.Props, 'title' | 'children' | 'className' | 'render'> & {
  /** Opens the drawer, for example a Button. Leave it out when something else controls `open`. */
  trigger?: ReactElement
  /** The edge it slides from. Swiping toward that edge dismisses it on touch. */
  side?: DrawerSide
  /** Side drawers only: default for panels and forms, narrow for navigation. */
  size?: DrawerSize
  /** Portal target, for example an active fullscreen root. Defaults to the document body. */
  container?: HTMLElement | null
  /** Classes for the drawer surface, for example a workspace panel style. */
  className?: string
}

/** A panel from the edge of the screen for a supporting task, such as inviting a camera, beside the work it supports. */
export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(function Drawer(
  {
    open, defaultOpen, onOpenChange, onOpenChangeComplete, disablePointerDismissal, trigger, side = 'right', size = 'default',
    title, eyebrow, description, hideDescription, hideTitle = false, footer, children, container, className, ...popup
  },
  ref,
) {
  return (
    <BaseDrawer.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      disablePointerDismissal={disablePointerDismissal}
      swipeDirection={swipeDirection[side]}
    >
      {trigger && <BaseDrawer.Trigger render={trigger} />}
      <BaseDrawer.Portal container={container ?? undefined}>
        <BaseDrawer.Backdrop className="play-drawer__backdrop" />
        <BaseDrawer.Viewport className={`play-drawer__viewport play-drawer__viewport--${side}`}>
          <BaseDrawer.Popup
            ref={ref}
            className={cn(
              'play-theme play-drawer',
              `play-drawer--${side}`,
              size === 'narrow' && side !== 'bottom' && 'play-drawer--narrow',
              hideTitle && 'play-drawer--title-hidden',
              className,
            )}
            {...popup}
          >
            <BaseDrawer.Content className="play-drawer__content">
              <DialogLayout title={title} eyebrow={eyebrow} description={description} hideDescription={hideDescription} hideTitle={hideTitle} footer={footer}>
                {children}
              </DialogLayout>
            </BaseDrawer.Content>
          </BaseDrawer.Popup>
        </BaseDrawer.Viewport>
      </BaseDrawer.Portal>
    </BaseDrawer.Root>
  )
})

/** A footer button that closes its Drawer, such as Cancel or Done. The same component as DialogClose. */
export const DrawerClose = DialogClose
